import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Checkbox} from '@Components/checkbox/checkbox';
import {CheckboxSize} from '@Components/checkbox/checkbox.types';
import {SelectableOptionRow} from '@Components/social-media-wizard/components/selectable-option-row/selectable-option-row';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {ToggleSwitchStyleVariant} from '@Components/toggle-switch-v2/toggle-switch-v2.types';
import {
  disableNextButton,
  enableNextButton,
  removeAccountFromPublishingParams,
  removeAccountIdFromPublishing,
  removeWizardSocialAccount,
  updatePublishingParamDisableComment,
  updatePublishingParamDisableDuet,
  updatePublishingParamDisableStitch,
  updatePublishingParamIsPromotingThirdParty,
  updatePublishingParamIsSelfBrand,
  updatePublishingParams,
  updateWizardSocialAccount,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {TiktokPrivacySelector} from '@Components/social-media-wizard/components/tiktok-privacy-selector';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {useAccountIdsToPublishTo, usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import type {TikTokPublishingParams} from '@Components/social-media/publish-params.types';
import {useWizardDesignsGraphicsMetaMap} from '@Components/social-media-wizard/social-media-wizard.hooks';
import type {ContentDisclosureOption, TiktokPublishingOptionsProps, TikTokAccountMetaUpdateResponse} from './tiktok-publishing-options.types';
import {
  TIKTOK_ALLOW_USERS_OPTION_SKELETON_HEIGHT,
  TIKTOK_ALLOW_USERS_OPTION_SKELETON_WIDTH,
  TIKTOK_DISCLOSURE_TOGGLE_SKELETON_HEIGHT,
  TIKTOK_DISCLOSURE_TOGGLE_SKELETON_WIDTH,
  TIKTOK_ALLOW_USERS_OPTION_SKELETON_CORNER_RADIUS,
  TIKTOK_TOGGLE_SKELETON_CORNER_RADIUS,
} from './tiktok-publishing-options.types';
import styles from './tiktok-publishing-options.module.scss';
import {useAppDispatch} from '@/hooks';
import type {TikTokPrivacySettings} from '@/components/social-media/post.vo';
import {TikTokPrivateAccountPrivacySettings, TikTokPublicAccountPrivacySettings} from '@/components/social-media/post.vo';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {
  addConnectAccountBroadcastChannel,
  isSocialMediaAccountSharedByTeamMember,
  reAuthenticateInModal,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media-account.library';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';

export function TiktokPublishingOptions({account}: TiktokPublishingOptionsProps): ReactElement | null {
  const {tikTokMeta} = account;
  const [disableComment, setDisableComment] = useState(tikTokMeta ? tikTokMeta.isCommentDisabled : false);
  const [disableStitch, setDisableStitch] = useState(tikTokMeta ? tikTokMeta.isStitchDisabled : false);
  const [disableDuet, setDisableDuet] = useState(tikTokMeta ? tikTokMeta.isDuetDisabled : false);
  const [contentDisclosureToggle, setContentDisclosureToggle] = useState(false);
  const [isSelfBrand, setIsSelfBrand] = useState(false);
  const [isPromotingThirdParty, setIsPromotingThirdParty] = useState(false);
  const [isBrandSwitchDisabled, setIsBrandSwitchDisabled] = useState(false);
  const [isTikTokAPIQueried, setIsTikTokAPIQueried] = useState(false);
  const [isAccountValid, setIsAccountValid] = useState(true);
  const dispatch = useAppDispatch();
  const publishingParams = usePostPublishingParamsForAccount(account.id) as TikTokPublishingParams;
  const designsMetaMap = useWizardDesignsGraphicsMetaMap();
  const singleAccountToPublishTo = useAccountIdsToPublishTo().length === 1;

  const onDisableComment = (): void => {
    const newDisableComment = !disableComment;

    setDisableComment(newDisableComment);
    dispatch(
      updatePublishingParamDisableComment({
        idSocialMediaAccount: account.id,
        disableComment: newDisableComment,
      })
    );
  };

  const onDisableStitch = (): void => {
    const newDisableStitch = !disableStitch;

    setDisableStitch(newDisableStitch);
    dispatch(
      updatePublishingParamDisableStitch({
        idSocialMediaAccount: account.id,
        disableStitch: newDisableStitch,
      })
    );
  };

  const onDisableDuet = (): void => {
    const newDisableDuet = !disableDuet;

    setDisableDuet(newDisableDuet);
    dispatch(
      updatePublishingParamDisableDuet({
        idSocialMediaAccount: account.id,
        disableDuet: newDisableDuet,
      })
    );
  };

  const doRefreshTikTokOptions = async (): Promise<void> => {
    try {
      const data = (await window.PMW.writeLocal(`socialmedia/updateTikTokAccountMeta/${account.id}`)) as TikTokAccountMetaUpdateResponse;

      dispatch(updateWizardSocialAccount(data.account));
      setIsTikTokAPIQueried(true);
      setIsAccountValid(true);
      dispatch(enableNextButton());
    } catch {
      setIsAccountValid(false);

      if (singleAccountToPublishTo) {
        dispatch(disableNextButton());
      }
    }
  };

  const onIsSelfBrand = (): void => {
    const newIsSelfBrand = !isSelfBrand;

    setIsSelfBrand(newIsSelfBrand);
    dispatch(
      updatePublishingParamIsSelfBrand({
        idSocialMediaAccount: account.id,
        isSelfBrand: newIsSelfBrand,
      })
    );
  };

  const onIsPromotingThirdParty = (): void => {
    const newIsPromotingThirdParty = !isPromotingThirdParty;

    setIsPromotingThirdParty(newIsPromotingThirdParty);
    dispatch(
      updatePublishingParamIsPromotingThirdParty({
        idSocialMediaAccount: account.id,
        isPromotingThirdParty: newIsPromotingThirdParty,
      })
    );
  };

  const onContentDisclosureToggle = (): void => {
    setContentDisclosureToggle(!contentDisclosureToggle);
  };

  const isThereAnyVideoPoster = (): boolean => {
    return Object.values(designsMetaMap).some((pageGraphicsMetaMap) => {
      return Object.values(pageGraphicsMetaMap).some((graphicMeta) => {
        return graphicMeta.isVideo;
      });
    });
  };

  const getOptionRowLabel = (icon: string, label: string): ReactElement => {
    return (
      <div className={styles.tiktokOptionRowLabel}>
        <Icon icon={icon} type={IconType.NONE} size={IconSize.SIZE_ICON_16} />
        <Text val={label} size={TextSize.MD} className="ct-neutral-primary" />
      </div>
    );
  };

  const getPrivacyRow = (): ReactElement => {
    return (
      <div className={styles.tiktokOptionRow}>
        {getOptionRowLabel('icon-globe', window.i18next.t('pmwjs_privacy'))}
        <TiktokPrivacySelector account={account} onSelectOption={onTikTokPrivacySettingsSelected} isLoading={!isTikTokAPIQueried} />
      </div>
    );
  };

  const getAllowUsersToRow = (): ReactElement => {
    return (
      <div className={styles.tiktokOptionRow}>
        {getOptionRowLabel('icon-group', window.i18next.t('pmwjs_allow_users_to'))}
        <div className={styles.tiktokAllowUsersOptions}>
          {getAllowUsersOption(window.i18next.t('pmwjs_comment'), disableComment, onDisableComment)}
          {getVideoOnlyAllowUsersOptions()}
        </div>
      </div>
    );
  };

  const getVideoOnlyAllowUsersOptions = (): ReactElement | null => {
    if (!isThereAnyVideoPoster()) {
      return null;
    }

    return (
      <>
        {getAllowUsersOption(window.i18next.t('pmwjs_duet'), disableDuet, onDisableDuet)}
        {getAllowUsersOption(window.i18next.t('pmwjs_stitch'), disableStitch, onDisableStitch)}
      </>
    );
  };

  const getAllowUsersOption = (label: string, isChecked: boolean, onToggle: VoidFunction): ReactElement => {
    if (!isTikTokAPIQueried) {
      return (
        <SkeletonBrick
          width={TIKTOK_ALLOW_USERS_OPTION_SKELETON_WIDTH}
          height={TIKTOK_ALLOW_USERS_OPTION_SKELETON_HEIGHT}
          radius={TIKTOK_ALLOW_USERS_OPTION_SKELETON_CORNER_RADIUS}
        />
      );
    }

    return (
      <SelectableOptionRow className={styles.allowUsersOption} isChecked={isChecked} onToggle={onToggle} checkboxAriaLabel={label}>
        <Text val={label} size={TextSize.MD} className="ct-neutral-primary" />
      </SelectableOptionRow>
    );
  };

  const getContentDisclosureSection = (): ReactElement => {
    return (
      <div className={styles.contentDisclosureSection}>
        <div className={styles.tiktokOptionRow}>
          {getOptionRowLabel('icon-video-design', window.i18next.t('pmwjs_content_disclosure_and_ads'))}
          {getContentDisclosureSwitch()}
        </div>
        {getContentDisclosureOptions()}
      </div>
    );
  };

  const getContentDisclosureSwitch = (): ReactElement => {
    if (!isTikTokAPIQueried) {
      return (
        <SkeletonBrick
          width={TIKTOK_DISCLOSURE_TOGGLE_SKELETON_WIDTH}
          height={TIKTOK_DISCLOSURE_TOGGLE_SKELETON_HEIGHT}
          radius={TIKTOK_TOGGLE_SKELETON_CORNER_RADIUS}
        />
      );
    }

    return (
      <ToggleSwitch
        isDisabled={isBrandSwitchDisabled}
        isChecked={contentDisclosureToggle}
        onChange={onContentDisclosureToggle}
        ariaLabel={window.i18next.t('pmwjs_content_disclosure_and_ads')}
        size={ToggleSwitchV2Size.MEDIUM}
        variant={ToggleSwitchStyleVariant.COMPACT}
      />
    );
  };

  const getTextForTikTokAgreement = (): string => {
    if (isSelfBrand && !isPromotingThirdParty) {
      return window.i18next.t('pmwjs_agreement_music_tiktok');
    }

    return window.i18next.t('pmwjs_agreement_brand_and_music_tiktok');
  };

  const onTikTokPrivacySettingsSelected = (selectedOption: TikTokPrivacySettings): void => {
    if (selectedOption === TikTokPublicAccountPrivacySettings.SELF_ONLY || selectedOption === TikTokPrivateAccountPrivacySettings.SELF_ONLY) {
      setIsBrandSwitchDisabled(true);
      setIsPromotingThirdParty(false);
      setContentDisclosureToggle(false);
      setIsSelfBrand(false);
    } else {
      setIsBrandSwitchDisabled(false);
    }
  };

  const getContentDisclosureOption = ({title, description, isChecked, onToggle}: ContentDisclosureOption): ReactElement => {
    return (
      <div className={styles.contentDisclosureOption}>
        <div className={styles.firstTextLineAlignedControl}>
          <Checkbox size={CheckboxSize.SM} isChecked={isChecked} onChange={onToggle} ariaLabel={title} />
        </div>
        <div className={styles.contentDisclosureOptionText}>
          <Text val={title} size={TextSize.MD} bold className="ct-neutral-primary" />
          <Text val={description} size={TextSize.MD} className="ct-neutral-secondary" />
        </div>
      </div>
    );
  };

  const getContentDisclosureOptions = (): ReactElement => {
    return (
      <div className={`${styles.contentDisclosurePanel} ${contentDisclosureToggle ? styles.contentDisclosurePanelVisible : ''}`}>
        <div className={styles.contentDisclosurePanelClip}>
          <div className={styles.contentDisclosurePanelContent}>
            {getContentDisclosureOption({
              title: window.i18next.t('pmwjs_your_brand'),
              description: window.i18next.t('pmwjs_video_labeled_promotional'),
              isChecked: isSelfBrand,
              onToggle: onIsSelfBrand,
            })}
            {getContentDisclosureOption({
              title: window.i18next.t('pmwjs_branded_content'),
              description: window.i18next.t('pmwjs_your_video_paid_partnership'),
              isChecked: isPromotingThirdParty,
              onToggle: onIsPromotingThirdParty,
            })}
            <div className={styles.tiktokAgreement}>
              <div className={styles.firstTextLineAlignedControl}>
                <Icon icon="icon-info" type={IconType.NONE} size={IconSize.SIZE_ICON_16} />
              </div>
              <Text className={styles.tiktokAgreementText} dangerouslySetInnerHTML val={getTextForTikTokAgreement()} size={TextSize.MD} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getTikTokPublishingOptions = (): ReactElement => {
    return (
      <div className={styles.tiktokOptionsList}>
        {getPrivacyRow()}
        {getAllowUsersToRow()}
        {getContentDisclosureSection()}
      </div>
    );
  };

  const getTikTokInvalidAccountMessage = (): ReactElement => {
    return (
      <div>
        <div className={styles.invalidAccountMessage}>
          <Icon icon="icon-exclamation-triangle" type={IconType.NONE} size={IconSize.SIZE_ICON_16} className="ct-error-primary" />
          <Text val={window.i18next.t('pmwjs_social_media_tiktok_reconnect')} size={TextSize.MD} className="ct-neutral-primary" />
        </div>
        <FieldSpacer />
        <div className={styles.accountCtaContainer}>
          <Button
            type={Type.PRIMARY}
            size={Size.XSMALL}
            iconPlacement={IconPlacement.LEFT}
            icon="icon-rotate-ccw"
            text={window.i18next.t('pmwjs_refresh')}
            title={window.i18next.t('pmwjs_refresh')}
            onClick={() => {
              reAuthenticateInModal(account.id, SocialPlatform.TIKTOK);
            }}
          />
          {getAccountDeleteButton()}
        </div>
      </div>
    );
  };

  const getAccountDeleteButton = (): ReactElement | null => {
    if (isSocialMediaAccountSharedByTeamMember(account)) {
      return null;
    }

    return (
      <Button
        type={Type.SECONDARY}
        size={Size.XSMALL}
        iconPlacement={IconPlacement.LEFT}
        icon="icon-delete"
        iconClassName="ct-error-primary"
        text={window.i18next.t('pmwjs_remove')}
        title={window.i18next.t('pmwjs_remove')}
        onClick={removeAccount}
      />
    );
  };

  const doRemoveAccountFromPost = (): void => {
    void window.PMW.writeLocal('socialmedia/deleteAccountForUser', {
      id: account.id,
    }).then((): void => {
      if (singleAccountToPublishTo) {
        dispatch(updatePublishingParams({}));
        dispatch(updateWizardStep(SocialMediaWizardStep.SELECT_ACCOUNTS));
      } else {
        dispatch(removeAccountIdFromPublishing(account.id));
        dispatch(removeAccountFromPublishingParams(account.id));
      }

      dispatch(removeWizardSocialAccount(account.id));
      trackPublishingGA4Events(GA4EventName.ACCOUNT_DELETED, {[GA4EventParamName.PLATFORM]: getSocialPlatformName(SocialPlatform.TIKTOK)});
    });
  };

  const removeAccount = (): void => {
    openConfirmDeleteModal({
      title: window.i18next.t('pmwjs_are_you_sure'),
      text: window.i18next.t('pmwjs_social_account_deletion_message'),
      ctaText: window.i18next.t('pmwjs_delete_account'),
      onDeleteConfirmation: doRemoveAccountFromPost,
    });
  };

  const onAccountReconnect = (): void => {
    void doRefreshTikTokOptions();
  };

  useEffect(() => {
    let accountCreatedBroadcastChannel: BroadcastChannel | undefined = undefined;

    if (publishingParams) {
      setIsSelfBrand(publishingParams.isSelfBrand);
      setIsPromotingThirdParty(publishingParams.isPromotingThirdParty);
      setDisableComment(publishingParams.disableComment);
      setDisableStitch(publishingParams.disableStitch);
      setDisableDuet(publishingParams.disableDuet);

      if (publishingParams.isSelfBrand || publishingParams.isPromotingThirdParty) {
        setContentDisclosureToggle(true);
      }

      accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(onAccountReconnect);
    }

    void doRefreshTikTokOptions();

    return (): void => {
      if (accountCreatedBroadcastChannel) {
        removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
      }
    };
  }, []);

  return isAccountValid ? getTikTokPublishingOptions() : getTikTokInvalidAccountMessage();
}
